import React, { useEffect, useId, useRef, useState } from "react";
import { useDebounced } from "@/components/AgentDirectorySearch";
import { milesLabel, slugify } from "@/components/DirectoryBreadcrumb";
import { useDirectoryBrokerages } from "@/hooks/useAgentDirectory";
import "./DirectoryBrokerageSheet.scss";

const SEARCH_DELAY_MS = 200;

const Chevron = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
    <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const whereLabel = (brokerage, placeName) =>
  placeName && brokerage?.town && slugify(brokerage.town) === slugify(placeName)
    ? `In ${brokerage.town}`
    : [brokerage?.town, Number.isFinite(Number(brokerage?.miles)) && brokerage?.miles !== null ? milesLabel(brokerage.miles) : ""]
    .filter(Boolean)
    .join(", ");

const DirectoryBrokerageSheet = ({ value, location, placeName, onChange }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const wrapRef = useRef(null);
  const toggleRef = useRef(null);
  const inputRef = useRef(null);
  const endRef = useRef(null);
  const titleId = useId();
  const typed = text.trim();
  const q = useDebounced(typed, SEARCH_DELAY_MS);
  const query = useDirectoryBrokerages(location, q, open);
  const brokerages = (query.data?.pages || []).flatMap((page) => page?.brokerages || []);
  const waiting = query.isLoading || (typed && q !== typed);

  const close = (refocus = true) => {
    setOpen(false);
    setText("");
    if (refocus) toggleRef.current?.focus();
  };

  const pick = (name) => {
    onChange(name);
    close();
  };

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    const node = endRef.current;
    if (!open || !node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && query.hasNextPage && !query.isFetchingNextPage) {
        query.fetchNextPage();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      if (text) setText("");
      else close();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const items = [...(wrapRef.current?.querySelectorAll(".directory-brokerages__row") || [])];
    if (!items.length) return;
    event.preventDefault();
    const at = items.indexOf(document.activeElement);
    const next = event.key === "ArrowDown" ? Math.min(items.length - 1, at + 1) : at <= 0 ? -1 : at - 1;
    if (next < 0) inputRef.current?.focus();
    else items[next].focus();
  };

  return (
    <div className="directory-brokerages" ref={wrapRef} onKeyDown={open ? onKeyDown : undefined}>
      <button
        type="button"
        ref={toggleRef}
        className={`directory-filters__chip${value ? " is-on" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span className="directory-brokerages__value">{value || "Brokerage"}</span>
        <Chevron />
      </button>
      {open ? (
        <>
          <div className="directory-brokerages__backdrop" aria-hidden="true" onClick={() => close()} />
          <div className="directory-brokerages__panel" role="dialog" aria-labelledby={titleId}>
            <div className="directory-brokerages__head">
              <p id={titleId} className="directory-brokerages__title">
                {placeName ? `Brokerages near ${placeName}` : "Brokerages near here"}
              </p>
              <button type="button" className="directory-brokerages__close" onClick={() => close()}>
                Done
              </button>
            </div>
            <div className="directory-brokerages__search">
              <input
                type="search"
                ref={inputRef}
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="Find a brokerage"
                aria-label="Find a brokerage"
                autoComplete="off"
              />
            </div>
            <div className="directory-brokerages__list">
              {typed ? null : (
                <button
                  type="button"
                  className={`directory-brokerages__row${value ? "" : " is-on"}`}
                  aria-pressed={!value}
                  onClick={() => pick("")}
                >
                  <span className="directory-brokerages__name">All brokerages</span>
                </button>
              )}
              {waiting && !brokerages.length
                ? Array.from({ length: 6 }, (_, index) => (
                    <span key={index} className="directory-brokerages__row is-loading" aria-hidden="true">
                      <span className="directory-brokerages__bar" />
                      <span className="directory-brokerages__bar is-short" />
                    </span>
                  ))
                : brokerages.map((brokerage) => {
                    const on = brokerage.name === value;
                    return (
                      <button
                        type="button"
                        key={`${brokerage.name}-${brokerage.town || ""}`}
                        className={`directory-brokerages__row${on ? " is-on" : ""}`}
                        aria-pressed={on}
                        onClick={() => pick(brokerage.name)}
                      >
                        <span className="directory-brokerages__name">{brokerage.name}</span>
                        {whereLabel(brokerage, placeName) ? (
                          <span className="directory-brokerages__where">{whereLabel(brokerage, placeName)}</span>
                        ) : null}
                      </button>
                    );
                  })}
              {query.isError && !brokerages.length ? (
                <div className="directory-brokerages__note">
                  <p>Brokerages did not load.</p>
                  <button type="button" onClick={() => query.refetch()}>
                    Try again
                  </button>
                </div>
              ) : null}
              {!waiting && !query.isError && !brokerages.length ? (
                <p className="directory-brokerages__note">
                  {typed ? `No brokerage near here matches “${typed}”` : "No brokerages listed near here yet"}
                </p>
              ) : null}
              {query.hasNextPage ? (
                <button
                  type="button"
                  ref={endRef}
                  className="directory-brokerages__more"
                  disabled={query.isFetchingNextPage}
                  onClick={() => query.fetchNextPage()}
                >
                  {query.isFetchingNextPage ? "Loading" : "Show more brokerages"}
                </button>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default DirectoryBrokerageSheet;
